/**
 * Shared category destination resolver (R1–R3, R6).
 *
 * Pure: hub vs PLP from `childrenIds` only. Single source for catalog cards,
 * mega-menu, hubs, breadcrumbs — no parallel leaf checks.
 *
 * R1 / R3 on the current model: `CategoryDto` has no `isLeaf` flag, so
 * leaf-ness is `childrenIds.length === 0`. An "empty non-leaf" (R3) is
 * indistinguishable from a leaf and correctly falls through to `'plp'` —
 * never an empty hub. If `childrenIds` is non-empty but children are missing
 * from `allByIds`, that is a hub-page data-loading concern (Phase 6), not
 * this resolver.
 *
 * R6: hubs never expose an "all products in branch" destination; the resolver
 * only returns page type, never a branch-wide PLP shortcut.
 */
export type CategoryDestination = 'hub' | 'plp'

export type CategoryDestinationInput = {
  childrenIds?: number[] | null
}

/**
 * R1: `childrenIds.length === 0` → `'plp'` (leaf)
 * R2: non-empty `childrenIds` → `'hub'` (never auto-open a branch-wide wall)
 * R3: empty / missing children on a supposed non-leaf → `'plp'` fallback
 *     (same code path as R1 on the current model — see file header)
 */
export function resolveCategoryDestination(
  category: CategoryDestinationInput,
): CategoryDestination {
  const childrenIds = category.childrenIds ?? []

  if (childrenIds.length === 0) {
    return 'plp'
  }

  return 'hub'
}
